import { useState } from "react";
import { ArrowDown, ArrowUp, Check, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { ChalkActionButton, ChalkCard, SectionTitle } from "../chalk/primitives";
import { tournaments } from "../../lib/tournamentApi";
import FormatEditor from "./FormatEditor";
import { Pill, inputStyle, pillBtn } from "./shared";

export default function DivisionsTab({ data, onChanged }) {
  const t = data.tournament;
  const [name, setName] = useState("");
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState("");
  const [open, setOpen] = useState(null);
  const divs = data.divisions;

  async function run(fn, ok) {
    try { await fn(); if (ok) toast.success(ok); await onChanged(); }
    catch (e) { toast.error(e.message || "Couldn't save."); }
  }
  function move(i, dir) {
    const order = divs.map((d) => d.id);
    [order[i], order[i + dir]] = [order[i + dir], order[i]];
    run(() => tournaments.reorderDivisions(t.id, order));
  }

  return (
    <div data-testid="divisions-tab">
      <SectionTitle label={`Divisions (${divs.length})`} />
      {divs.map((d, i) => {
        const count = data.teams.filter((x) => x.division_id === d.id).length;
        return (
          <div key={d.id}>
          <ChalkCard data-testid={`division-row-${d.id}`} style={{ marginBottom: 6, display: "flex", alignItems: "center", gap: 8 }}>
            {editing === d.id ? (
              <>
                <input value={draft} onChange={(e) => setDraft(e.target.value)} data-testid={`division-rename-input-${d.id}`}
                       style={{ ...inputStyle, flex: 1 }} />
                <button aria-label="Save name" data-testid={`division-rename-save-${d.id}`} style={_icon}
                        onClick={() => run(() => tournaments.patchDivision(t.id, d.id, { name: draft }), "Renamed.").then(() => setEditing(null))}>
                  <Check size={15} aria-hidden="true" />
                </button>
              </>
            ) : (
              <>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600 }}>{d.name}</div>
                  <div style={{ fontSize: 11, color: "var(--chalk-muted)" }}>{count} team{count === 1 ? "" : "s"} · {({ single_elim: "Single elim", pools: "Pools → bracket", double_elim: "Double elim" })[d.format]}</div>
                </div>
                <Pill tone={d.status === "complete" ? "green" : d.status === "live" ? "amber" : "muted"}>{d.status}</Pill>
                <button aria-label="Rename" data-testid={`division-rename-${d.id}`} style={_icon}
                        onClick={() => { setEditing(d.id); setDraft(d.name); }}><Pencil size={14} aria-hidden="true" /></button>
                <button aria-label="Move up" disabled={i === 0} data-testid={`division-up-${d.id}`} style={_icon} onClick={() => move(i, -1)}>
                  <ArrowUp size={14} aria-hidden="true" /></button>
                <button aria-label="Move down" disabled={i === divs.length - 1} data-testid={`division-down-${d.id}`} style={_icon} onClick={() => move(i, 1)}>
                  <ArrowDown size={14} aria-hidden="true" /></button>
                {d.id !== t.default_division_id && (
                  <button aria-label="Delete division" data-testid={`division-delete-${d.id}`} style={{ ..._icon, color: "var(--chalk-red)" }}
                          onClick={() => window.confirm(`Delete ${d.name}?`) && run(() => tournaments.deleteDivision(t.id, d.id), "Deleted.")}>
                    <Trash2 size={14} aria-hidden="true" /></button>
                )}
                <button data-testid={`division-format-${d.id}`} onClick={() => setOpen(open === d.id ? null : d.id)}
                        style={pillBtn(open === d.id)}>Format</button>
              </>
            )}
          </ChalkCard>
          {open === d.id && <FormatEditor data={data} division={d} onChanged={onChanged} />}
          </div>
        );
      })}
      <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="U12 Boys, Gold, Rec…"
               data-testid="division-name-input" style={{ ...inputStyle, flex: 1 }} />
        <ChalkActionButton label="Add division" fullWidth={false} testId="division-add"
                           onClick={() => name.trim() && run(() => tournaments.createDivision(t.id, name.trim()), "Division added.").then(() => setName(""))} />
      </div>
      <div style={{ fontSize: 11, color: "var(--chalk-muted)", marginTop: 8 }}>
        Each division has its own teams and bracket (up to {t.teams_cap} teams). Locations are shared.
      </div>
    </div>
  );
}

const _icon = { background: "transparent", border: "0.5px solid var(--chalk-border)", borderRadius: 8, padding: "5px 7px",
                cursor: "pointer", color: "var(--chalk-board)" };
